import { ReactNode } from 'react'
import {
  Card,
  Progress,
  Text,
  Button,
  Icon,
  Image,
  CardColors,
  Separator,
} from '@/ui'
import { Tooltip } from '@/features/taxes/quarterly-tax-estimates/components/Tooltip'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useReselector } from '@/utils/sharedHooks'
import { isFreeTrialPromoCode } from '@/selectors/user.selectors'
import { TypographyStyles } from '@/styles'

const SETUP_STEPS = [
  'Connect bank accounts',
  'Bank account information',
  'Upload monthly statements',
]

const FREE_TRIAL_SETUP_STEPS = [
  'Connect bank accounts',
  'Bank account information',
]

export const SetupCard = ({
  currentStep,
  children,
  continueDisabled = false,
  onBackClick,
  onContinueClick,
  onSkipClick,
  showSkipButton = true,
  isSkipForNow = false,
  completeButtonContent,
  continueDisabledMessage,
}: {
  currentStep: number
  children?: ReactNode
  continueDisabled?: boolean
  onBackClick?: () => void
  onContinueClick?: () => void
  onSkipClick?: () => void
  showSkipButton?: boolean
  isSkipForNow?: boolean
  completeButtonContent?: string
  continueDisabledMessage?: string
}) => {
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)
  const skipText = isSkipForNow ? 'Skip For Now' : 'Skip'
  const setupCardSteps = hasFreeTrialPromoCode
    ? FREE_TRIAL_SETUP_STEPS
    : SETUP_STEPS
  const setupCardHeader = hasFreeTrialPromoCode
    ? 'Get Started With Your Free Trial'
    : 'Get Started With Heard'

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col space-y-4">
        <div className="flex flex-row items-start gap-4 px-8 pt-12 pb-4">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/books_shadow.svg"
            alt="books"
            width={80}
            height={80}
            objectFit="scaleDown"
          />

          <div className="flex-grow">
            <div className="flex justify-between items-start">
              <Text variant={TypographyStyles.H2}>{setupCardHeader}</Text>
              <div className="flex items-center gap-2">
                <Icon icon={light('check')} color="green" />
                <Text
                  variant={TypographyStyles.BODY_XS}
                  className="text-green-600"
                >
                  Autosaved
                </Text>
              </div>
            </div>

            <Progress
              variant="segmented"
              numberOfSegments={setupCardSteps.length}
              activeSegment={currentStep}
              segmentTitles={setupCardSteps}
            />
          </div>
        </div>

        <Separator />

        <div className="px-8 pt-2 pb-6">{children}</div>

        <Separator />

        <div className="flex flex-wrap items-center justify-between px-8 py-6 gap-4">
          <div className="flex-grow">
            {onBackClick && (
              <Button variant="link" onClick={onBackClick}>
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-3 items-center">
            {showSkipButton && onSkipClick && (
              <Button variant="secondary" onClick={onSkipClick}>
                {skipText}
              </Button>
            )}

            {onContinueClick &&
              (continueDisabled ? (
                <Tooltip
                  popup={{
                    body: continueDisabledMessage || 'Continue disabled',
                  }}
                  labelComponent={
                    <Button disabled size="lg">
                      {completeButtonContent || 'Continue'}
                    </Button>
                  }
                />
              ) : (
                <Button onClick={onContinueClick} size="lg">
                  {completeButtonContent || 'Continue'}
                </Button>
              ))}
          </div>
        </div>
      </div>
    </Card>
  )
}
